/**
 * The rows of the Labels drawer and of its sheet: each variable of the
 * catalogue (the questionnaire's questions first, then the variables of the
 * data), each followed by its categories, grid rows, cells or loop
 * iterations, then the shown labels that name nothing of the project. Pure:
 * the drawer keeps the row objects stable (`reuseRows`) and filters them
 * (`listLabelRows`); a new name is checked against the names the other items
 * of its variable keep or are shown under (`othersOf`, `takenNames`), the
 * names the compiler gives way to (compiler/table.ts `rowNames`).
 */
import { nameKey } from "../../compiler/names"
import {
  dataVariableParts,
  describeDataVariable,
  isYesNoField,
} from "../../model/dataVariables"
import { instanceWord, labelKey, LabelTarget, targetOf } from "../../model/shownLabels"
import type { Catalogue, CatalogueQuestion } from "../../types/catalogue"
import type { ShownLabel } from "../../types/tabSpecDocument"

/** What an item of a variable is called: an option, a grid row, a numeric cell, a loop iteration. */
export type ItemWord = "category" | "row" | "cell" | "iteration"

export type LabelRow =
  | {
      kind: "variable"
      key: string
      target: LabelTarget
      raw: string
      shown: string | null
      /** "question", "hidden question", or what `describeDataVariable` says. */
      phrase: string
      /** The last part of the label (`dataVariableParts`): the whole label of a question. */
      name: string
      /** The containers above a nested variable of the data, in words; null at the top level. */
      within: string | null
    }
  | {
      kind: "item"
      key: string
      target: LabelTarget
      raw: string
      shown: string | null
      word: ItemWord
      /** The raw label of the item's variable (`target.variable`). */
      variable: string
    }
  | {
      kind: "gone"
      key: string
      target: LabelTarget
      /** The variable, and for an item ": " and the item. */
      raw: string
      shown: string
      /** The data is not loaded yet: the entry may still name a variable of the data. */
      waiting: boolean
    }
  | { kind: "heading"; key: string; text: string }

export type VariableRow = Extract<LabelRow, { kind: "variable" }>
export type ItemRow = Extract<LabelRow, { kind: "item" }>
export type GoneRow = Extract<LabelRow, { kind: "gone" }>

export const NOT_IN_PROJECT = "Not in this project"
export const WAITING_FOR_DATA = "Waiting for the data"

const phraseOf = (q: CatalogueQuestion): string =>
  q.source === "data" ? describeDataVariable(q) : q.hidden ? "hidden question" : "question"

/** A stored entry in words: the variable, then the option or the instance (its parts as its label joins them). */
const goneWords = (t: LabelTarget): string =>
  t.option !== undefined
    ? `${t.variable}: ${t.option}`
    : t.instance !== undefined
      ? `${t.variable}: ${t.instance.split("//").join(" - ")}`
      : t.variable

/**
 * Every variable of `catalogue` and its items, then the entries of `labels`
 * that name none of them: under "Waiting for the data" while the data is not
 * loaded (`knownPaths` null), else under "Not in this project". A lone yes or
 * no field has no item (its table shows Yes and No); a variable has instance
 * rows when it has more than one instance or one with an id. The first entry
 * of a target counts, shown trimmed. Keys are `labelKey` of the target, and
 * `heading:<text>` for a heading; a variable or item met twice is listed once.
 */
export const buildLabelRows = (
  catalogue: Catalogue,
  labels: ShownLabel[] | undefined
): LabelRow[] => {
  const stored = new Map<string, { target: LabelTarget; shown: string }>()
  ;(labels ?? []).forEach((l) => {
    const target = targetOf(l)
    const key = labelKey(target)
    if (!stored.has(key)) stored.set(key, { target, shown: l.shown.trim() })
  })
  const shownOf = (key: string): string | null => stored.get(key)?.shown || null

  const rows: LabelRow[] = []
  const listed = new Set<string>()
  const item = (target: LabelTarget, raw: string, word: ItemWord): void => {
    const key = labelKey(target)
    if (listed.has(key)) return
    listed.add(key)
    rows.push({ kind: "item", key, target, raw, shown: shownOf(key), word, variable: target.variable })
  }

  ;[...catalogue.questions, ...(catalogue.dataVariables ?? [])].forEach((q) => {
    const target: LabelTarget = { variable: q.label }
    const key = labelKey(target)
    if (!q.label || listed.has(key)) return
    listed.add(key)
    const { name, within } =
      q.source === "data" ? dataVariableParts(q) : { name: q.label, within: null }
    rows.push({
      kind: "variable",
      key,
      target,
      raw: q.label,
      shown: shownOf(key),
      phrase: phraseOf(q),
      name,
      within,
    })
    if (!isYesNoField(q))
      q.options.forEach((o) => {
        if (o.label) item({ variable: q.label, option: o.label }, o.label, "category")
      })
    if (q.instances.length > 1 || q.instances.some((i) => i.id)) {
      const word = instanceWord(q)
      q.instances.forEach((i) => {
        if (i.id) item({ variable: q.label, instance: i.id }, i.label || i.id, word)
      })
    }
  })

  const gone = [...stored.entries()].filter(([key]) => !listed.has(key))
  if (gone.length > 0) {
    const waiting = catalogue.knownPaths === null
    const text = waiting ? WAITING_FOR_DATA : NOT_IN_PROJECT
    rows.push({ kind: "heading", key: `heading:${text}`, text })
    gone.forEach(([key, { target, shown }]) =>
      rows.push({ kind: "gone", key, target, raw: goneWords(target), shown, waiting })
    )
  }
  return rows
}

/**
 * A variable as its row shows it, in words: the containers above a nested
 * variable of the data, then its name ("Overall Brand NPS: Bank 1: score");
 * the label itself at the top level.
 */
export const wordsOf = (row: VariableRow): string =>
  row.within ? `${row.within}: ${row.name}` : row.name

const matchesText = (needle: string, raw: string, shown: string | null): boolean =>
  raw.toLowerCase().includes(needle) || (shown ?? "").toLowerCase().includes(needle)

/**
 * The rows listed for a search (raw and shown labels, trimmed, in any case;
 * for a variable also the words its row shows, `wordsOf`) and "Changed
 * only". A variable that matches the search lists all its items;
 * an item that matches lists itself and its variable. "Changed only" keeps
 * the rows with a shown label and the variable row above them. The two
 * combine. A heading stays when a row under it stays. `rows` itself when
 * neither filter is on.
 */
export const listLabelRows = (
  rows: LabelRow[],
  search: string,
  changedOnly: boolean
): LabelRow[] => {
  const needle = search.trim().toLowerCase()
  if (!needle && !changedOnly) return rows
  const out: LabelRow[] = []
  /** The variable row of the items walked, until it is listed. */
  let parent: VariableRow | null = null
  let parentMatches = false
  /** The heading of the rows walked, until it is listed. */
  let heading: LabelRow | null = null
  for (const r of rows) {
    if (r.kind === "heading") {
      heading = r
      parent = null
      parentMatches = false
    } else if (r.kind === "variable") {
      parentMatches =
        !needle ||
        matchesText(needle, r.raw, r.shown) ||
        wordsOf(r).toLowerCase().includes(needle)
      if (parentMatches && (!changedOnly || r.shown !== null)) {
        out.push(r)
        parent = null
      } else parent = r
    } else if (r.kind === "item") {
      const found = parentMatches || matchesText(needle, r.raw, r.shown)
      if (!found || (changedOnly && r.shown === null)) continue
      if (parent) {
        out.push(parent)
        parent = null
      }
      out.push(r)
    } else {
      if (needle && !matchesText(needle, r.raw, r.shown)) continue
      if (heading) {
        out.push(heading)
        heading = null
      }
      out.push(r)
    }
  }
  return out
}

/**
 * The names the other items of the same variable and word keep or are shown
 * under: each one's raw label, and its shown label when it has one. A shown
 * label gives way to both in the tables (compiler/table.ts `rowNames`).
 */
export const othersOf = (rows: LabelRow[], row: ItemRow): string[] =>
  rows.flatMap((r) =>
    r.kind === "item" &&
    r.key !== row.key &&
    r.word === row.word &&
    r.target.variable === row.target.variable
      ? r.shown !== null
        ? [r.raw, r.shown]
        : [r.raw]
      : []
  )

/**
 * The names the items of each variable and word keep (their raw labels) or
 * are shown under, kept up to date while a bulk change is planned: `clash`
 * gives the name another item of `row`'s variable and word has that `text`
 * would clash with (compared as `nameKey`), or null; `show` records the name
 * `row` is now planned under (its raw label for a reset). The same check as
 * `othersOf`, without walking the variable for every item.
 */
export interface TakenNames {
  clash: (row: ItemRow, text: string) => string | null
  show: (row: ItemRow, name: string) => void
}

/** Name key to the items (by row key) that keep or show a name of that key, with the name. */
type NameBuckets = Map<string, Map<string, string>>
const NO_NAMES: Map<string, string> = new Map()

export const takenNames = (rows: LabelRow[]): TakenNames => {
  /** Per variable and word: the raw labels, and the shown names that differ from them. */
  const groups = new Map<string, { raw: NameBuckets; shown: NameBuckets }>()
  /** Each item's shown name now, when it differs from its raw label. */
  const current = new Map<string, string>()
  const groupOf = (r: ItemRow): { raw: NameBuckets; shown: NameBuckets } => {
    const id = JSON.stringify([r.target.variable, r.word])
    let group = groups.get(id)
    if (!group) {
      group = { raw: new Map(), shown: new Map() }
      groups.set(id, group)
    }
    return group
  }
  const put = (buckets: NameBuckets, name: string, key: string): void => {
    const k = nameKey(name)
    let bucket = buckets.get(k)
    if (!bucket) {
      bucket = new Map()
      buckets.set(k, bucket)
    }
    bucket.set(key, name)
  }
  const show = (r: ItemRow, name: string): void => {
    const { shown } = groupOf(r)
    const was = current.get(r.key)
    if (was !== undefined) shown.get(nameKey(was))?.delete(r.key)
    current.delete(r.key)
    if (nameKey(name) === nameKey(r.raw)) return
    put(shown, name, r.key)
    current.set(r.key, name)
  }
  rows.forEach((r) => {
    if (r.kind !== "item") return
    put(groupOf(r).raw, r.raw, r.key)
    if (r.shown !== null) show(r, r.shown)
  })
  const clash = (r: ItemRow, text: string): string | null => {
    const { raw, shown } = groupOf(r)
    const k = nameKey(text)
    for (const buckets of [shown, raw])
      for (const [key, name] of buckets.get(k) ?? NO_NAMES) if (key !== r.key) return name
    return null
  }
  return { clash, show }
}

const sameRow = (a: LabelRow, b: LabelRow): boolean => {
  switch (a.kind) {
    case "variable":
      return (
        b.kind === "variable" &&
        a.raw === b.raw &&
        a.shown === b.shown &&
        a.phrase === b.phrase &&
        a.name === b.name &&
        a.within === b.within
      )
    case "item":
      return (
        b.kind === "item" &&
        a.raw === b.raw &&
        a.shown === b.shown &&
        a.word === b.word &&
        a.variable === b.variable
      )
    case "gone":
      return b.kind === "gone" && a.raw === b.raw && a.shown === b.shown && a.waiting === b.waiting
    case "heading":
      return b.kind === "heading" && a.text === b.text
  }
}

/**
 * `rows` with the objects of `previous` (the last rows, by key) wherever a
 * row shows the same, so a cell that compares its row renders again only for
 * a row that changed: an edit of one label builds one row object. Returns the
 * rows and their index for the next call.
 */
export const reuseRows = (
  previous: Map<string, LabelRow>,
  rows: LabelRow[]
): { rows: LabelRow[]; index: Map<string, LabelRow> } => {
  const index = new Map<string, LabelRow>()
  const kept = rows.map((r) => {
    const was = previous.get(r.key)
    const row = was && sameRow(was, r) ? was : r
    index.set(r.key, row)
    return row
  })
  return { rows: kept, index }
}
